/**
 * LAS SOLICITUDES QUE LA CAJA LE ESCALA AL PROPIETARIO (PANEL-NOTIF-T1).
 *
 * AUDIENCIA: el propietario, en el panel de escritorio. Decide sobre algo que
 * un colaborador pidio en la caja y que la caja no deja hacer sola.
 *
 * EL CONTRATO NO ES DE ESTE ARCHIVO. Es de APR-T1 (Argy-D) y lo sirve NexoAPI:
 *   GET  /api/v1/solicitudes-aprobacion?businessId=biz_X
 *        -> {success, data:{items, total, page, pageSize, pendientesResolver}}
 *   POST /api/v1/solicitudes-aprobacion/{syncId}/resolver
 *        {businessId, veredicto: aprobada|rechazada, aprobadorNombre, aprobadorUuid?, nota?}
 *        -> 409 si ya tiene veredicto; el Desktop lo APLICA en su proximo ciclo.
 * Leido de NexoAPI main 5eeb79c el 15-sep.
 *
 * LA FECHA NO ES UN ADORNO. Las primeras 7 solicitudes reales (Exito Chino)
 * subieron al espejo el 15-sep pero son del 3 al 30 de agosto: se pidieron en
 * la caja y quedaron ahi hasta que la version 1226 las subio. Aprobar hoy la
 * anulacion de un egreso de hace 43 dias cambia una caja ya cerrada. Por eso
 * cada solicitud dice cuantos dias tiene, y la vista lo avisa antes de los
 * botones.
 *
 * Sin DOM: lo prueba scripts/check-bandeja-aprobaciones.mjs.
 */

/** Los tipos que manda la caja, en palabras del dueno. Medidos en las filas reales + los del contrato. */
export const ETIQUETAS = {
  anular_venta: 'Anular una venta',
  anular_egreso: 'Anular un egreso',
  modificar_egreso: 'Cambiar un egreso',
  modificar_factura: 'Cambiar una factura',
  cambiar_metodo_venta: 'Cambiar cómo se pagó una venta',
  cambiar_metodo_pago: 'Cambiar el método de pago',
  ajustar_inventario: 'Ajustar el inventario',
  // Medidos en el espejo de EC el 14-oct (APR-BOTONES-T1, Ares #26642): la caja ya los manda y se leian sin tilde.
  eliminar_arqueo: 'Borrar un arqueo',
  eliminar_movimiento: 'Borrar un movimiento de caja',
  anular_credito: 'Anular un crédito'
};

/**
 * SOBRE QUE MOVIMIENTO cae una solicitud: referencia_id es el id interno de la caja y cada tabla tiene su propia
 * numeracion (un egreso #18770 y una venta #18770 son cosas distintas). Dos pedidos se cruzan solo si tocan la misma
 * cosa. Un credito es una venta fiada (la 32 de EC: anular_credito sobre la venta 86134, Factura #86151) y una factura
 * es una venta, asi que van con las ventas.
 */
const MOVIMIENTO_DEL_TIPO = { venta: 'venta', factura: 'venta', credito: 'venta', egreso: 'egreso', arqueo: 'arqueo', movimiento: 'movimiento', abono: 'abono' };

export function sobreQueMovimiento(tipo) {
  const partes = String(tipo || '').split('_');
  const cola = partes[partes.length - 1];
  return MOVIMIENTO_DEL_TIPO[cola] || cola || 'otro';
}

/** Los unicos estados que el API deja resolver. */
export const ESTADOS_POR_DECIDIR = ['pendiente', 'enviada'];

/** Desde cuantos dias una solicitud se avisa como vieja. */
export const DIAS_PARA_AVISAR = 7;

/**
 * ¿Se ofrece decidir desde el panel? SI, desde el 30-sep (APR-BOTONES-T1, Ares #20584).
 *
 * Hasta ese dia estaba apagado por una razon medida: el 15-sep (#14989) la caja no tenia rama para
 * solicitud_aprobacion_resuelta, y un veredicto del panel se quedaba en la cola sin aplicarse. Desde el Desktop 1251
 * (APR-REMOTO-T1) la caja aplica el veredicto que llega del panel. Josse lo pidio el 30-sep: entro a #/notificaciones
 * con la solicitud 21 de EC (venta #85764) y no tenia como aprobarla.
 *
 * La fecha sigue a la vista: cada tarjeta dice cuantos dias tiene y las viejas avisan que esa caja puede estar ya
 * cuadrada. La confirmacion dice QUE cambia (que se pidio, sobre que movimiento) antes del si.
 */
export const DECIDIR_DESDE_PANEL = true;

export const RAZON_SIN_DECIDIR =
  'Por ahora se deciden en el programa de la caja: la caja todavía no recibe las decisiones que se toman aquí.';

const ESTADO_EN_PALABRAS = {
  pendiente: 'esperando tu decisión',
  enviada: 'esperando tu decisión',
  aprobada: 'aprobada, la caja la aplica al sincronizar',
  rechazada: 'rechazada',
  aplicada: 'aprobada y ya aplicada en la caja',
  fallida: 'aprobada, pero la caja no la pudo aplicar',
  expirada: 'venció sin decisión',
  cancelada: 'cancelada por quien la pidió'
};

/** Un tipo que nadie ha nombrado todavia se lee igual, sin guiones bajos. */
export function etiquetaDelTipo(tipo) {
  const t = String(tipo || '').trim();
  if (ETIQUETAS[t]) return ETIQUETAS[t];
  if (!t) return 'Solicitud';
  const palabras = t.split('_').join(' ');
  return palabras.charAt(0).toUpperCase() + palabras.slice(1);
}

export function estadoEnPalabras(estado) {
  const e = String(estado || '').toLowerCase();
  return ESTADO_EN_PALABRAS[e] || e || 'sin estado';
}

/** Una solicitud del API, tolerando las dos formas de la clave (al LEER se perdona). */
export function leerSolicitud(d) {
  const o = d || {};
  const v = (camel, pascal, snake) => {
    if (o[camel] !== undefined && o[camel] !== null) return o[camel];
    if (o[pascal] !== undefined && o[pascal] !== null) return o[pascal];
    if (snake && o[snake] !== undefined && o[snake] !== null) return o[snake];
    return null;
  };
  const tipo = String(v('tipo', 'Tipo') || '');
  // payload llega como objeto (el API lo re-parsea) o como texto crudo si no
  // parseo. Las claves de adentro son las de la caja: snake_case.
  let payload = v('payload', 'Payload');
  if (typeof payload === 'string') { try { payload = JSON.parse(payload); } catch (e) { payload = null; } }
  const ref = payload && typeof payload === 'object'
    ? (payload.referencia_id !== undefined ? payload.referencia_id : payload.referenciaId)
    : null;
  // APR-REFERENCIA-T1 (D): la referencia como la conoce el dueno ("Factura #85781 por $35.000"). referencia_id es el
  // id INTERNO de la caja: medido por D (#20596), la 21 apunta a la venta 85764 = factura 85781, y la factura VISIBLE
  // 85764 es otra venta. Mostrar el id interno como numero de factura engana.
  const refDesc = payload && typeof payload === 'object' ? (payload.referencia_desc || payload.referenciaDesc || '') : '';
  return {
    referencia: Number(ref) > 0 ? Number(ref) : null,
    referenciaDesc: String(refDesc || v('referenciaDesc', 'ReferenciaDesc', 'referencia_desc') || ''),
    // nexo-api 380 (A #20667): ¿el dia que la solicitud toca ya tiene arqueo? Con la regla de la caja (fecha del registro, si
    // no la fecha operativa). null = el API todavia no lo dice (la tarjeta queda como antes).
    diaArqueado: (() => { const x = v('diaArqueado', 'DiaArqueado', 'dia_arqueado'); return x === null ? null : x === true || x === 'true'; })(),
    fechaRevisada: v('fechaRevisada', 'FechaRevisada', 'fecha_revisada'),
    syncId: String(v('syncId', 'SyncId', 'sync_id') || ''),
    tipo,
    etiqueta: etiquetaDelTipo(tipo),
    estado: String(v('estado', 'Estado') || '').toLowerCase(),
    resumen: String(v('resumen', 'Resumen') || ''),
    motivo: String(v('motivo', 'Motivo') || ''),
    solicitante: String(v('solicitanteNombre', 'SolicitanteNombre', 'solicitante_nombre') || ''),
    fechaOperativa: v('fechaOperativa', 'FechaOperativa', 'fecha_operativa'),
    creada: v('createdAt', 'CreatedAt', 'created_at'),
    aprobador: v('aprobadorNombre', 'AprobadorNombre', 'aprobador_nombre'),
    error: v('errorMsg', 'ErrorMsg', 'error_msg')
  };
}

/**
 * El listado entero. Recibe el CUERPO JSON del API ({success, data:{...}}).
 * `pendientesSegunApi` es el conteo del servidor; si no coincide con lo que
 * llego en la pagina, la vista lo dice en vez de elegir uno.
 */
export function leerListado(cuerpo) {
  const c = cuerpo || {};
  const d = c.data || c.Data || c;
  const crudos = Array.isArray(d.items) ? d.items : (Array.isArray(d.Items) ? d.Items : (Array.isArray(d) ? d : []));
  const solicitudes = crudos.map(leerSolicitud);
  const porDecidir = solicitudes.filter((s) => ESTADOS_POR_DECIDIR.includes(s.estado));
  // Dos pedidos sobre el MISMO movimiento (medido: modificar y anular el egreso
  // #18770 el mismo dia). Decidir uno sin ver el otro puede aplicar los dos.
  const porReferencia = {};
  const llave = (s) => sobreQueMovimiento(s.tipo) + '#' + s.referencia;
  for (const s of porDecidir) if (s.referencia) porReferencia[llave(s)] = (porReferencia[llave(s)] || 0) + 1;
  for (const s of porDecidir) s.otrasSobreLoMismo = s.referencia ? porReferencia[llave(s)] - 1 : 0;
  const total = Number(d.total !== undefined ? d.total : (d.Total !== undefined ? d.Total : solicitudes.length));
  const pend = d.pendientesResolver !== undefined ? d.pendientesResolver : d.PendientesResolver;
  return {
    solicitudes,
    porDecidir,
    decididas: solicitudes.filter((s) => !ESTADOS_POR_DECIDIR.includes(s.estado)),
    total,
    pendientesSegunApi: pend === undefined || pend === null ? null : Number(pend),
    paginaIncompleta: total > solicitudes.length
  };
}

/**
 * Dias entre la fecha operativa (YYYY-MM-DD, la del negocio) y hoy. Se cuenta
 * por calendario, no por horas: una solicitud de ayer a las 11 p.m. tiene 1 dia.
 */
export function diasDesde(fecha, hoy = new Date()) {
  const partes = String(fecha || '').slice(0, 10).split('-').map(Number);
  if (partes.length !== 3 || partes.some((n) => !Number.isFinite(n) || n <= 0)) return null;
  const antes = Date.UTC(partes[0], partes[1] - 1, partes[2]);
  const ahora = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((ahora - antes) / 86400000);
}

/**
 * LA MAS VIEJA de las que esperan decision (Ares #26726): la de fecha operativa mas antigua. Es la que mas pesa,
 * porque su caja puede estar ya cuadrada. null si ninguna trae fecha legible.
 */
export function laMasVieja(porDecidir, hoy = new Date()) {
  let vieja = null;
  for (const s of porDecidir || []) {
    const dias = diasDesde(s.fechaOperativa, hoy);
    if (dias === null) continue;
    if (!vieja || dias > vieja.dias) vieja = { dias, fecha: String(s.fechaOperativa).slice(0, 10), etiqueta: s.etiqueta || 'Solicitud' };
  }
  return vieja;
}

/**
 * FRONT-VISTAS-EC-V04-T1 (Ares #30623; D midio 25 de EC esperando mas de 7 dias): la bandeja se parte por lo que
 * pasaria al aprobar. Una solicitud sobre un dia ya cuadrado la caja NO la aplica (queHaceLaCaja): mezclarla con las
 * que si se aplican hace aprobar a ciegas. Cada grupo va de la MAS VIEJA a la mas nueva (la que mas pesa primero).
 *   aplicables      -> diaArqueado === false
 *   sinSaber        -> diaArqueado === null (el API todavia no lo dice)
 *   sobreDiaCuadrado-> diaArqueado === true
 */
export function gruposParaDecidir(porDecidir, hoy = new Date()) {
  const edad = (s) => { const d = diasDesde(s.fechaOperativa, hoy); return d === null ? -1 : d; };
  const orden = (a, b) => edad(b) - edad(a);
  const lista = porDecidir || [];
  return {
    aplicables: lista.filter((s) => s.diaArqueado === false).sort(orden),
    sinSaber: lista.filter((s) => s.diaArqueado !== true && s.diaArqueado !== false).sort(orden),
    sobreDiaCuadrado: lista.filter((s) => s.diaArqueado === true).sort(orden)
  };
}

export function antiguedadEnPalabras(dias) {
  if (dias === null || dias === undefined) return 'sin fecha';
  if (dias <= 0) return 'de hoy';
  if (dias === 1) return 'de ayer';
  return 'de hace ' + dias + ' días';
}

/**
 * QUE CAMBIA si se aprueba: lo que se pidio y sobre que movimiento, en palabras del dueno. Es lo que la confirmacion
 * muestra antes del si (APR-BOTONES-T1): "Cambiar cómo se pagó una venta (movimiento #85764): <resumen>".
 */
export function queCambia(s) {
  const o = s || {};
  // Con la descripcion de la caja, esa (es la que el dueno reconoce); sin ella, el id dicho como INTERNO, nunca como
  // numero de factura.
  // Si el resumen ya trae la referencia de la caja (D #20602: resumen = Tipo: referencia_desc), no se repite.
  const yaEnResumen = o.referenciaDesc && String(o.resumen || '').includes(o.referenciaDesc);
  if (yaEnResumen) return String(o.resumen);
  const sobre = o.referenciaDesc ? ' (' + o.referenciaDesc + ')' : (o.referencia ? ' (referencia interna #' + o.referencia + ')' : '');
  return String(o.etiqueta || 'Solicitud') + sobre + (o.resumen ? ': ' + o.resumen : '');
}

/**
 * LO QUE LA CAJA HACE SOLA AL APROBAR (D #20596, matriz de diferibles del ejecutor del Desktop 1251): anular/reactivar
 * venta y egreso, eliminar un movimiento y anular/reactivar un abono. Los demas (cambiar el metodo de una venta, cambiar
 * un egreso...) quedan APROBADOS en la caja y quien los pidio hace el cambio a mano (tiene 24 h).
 */
export const TIPOS_QUE_LA_CAJA_APLICA = ['anular_venta', 'reactivar_venta', 'anular_egreso', 'reactivar_egreso', 'eliminar_movimiento', 'anular_abono', 'reactivar_abono'];

/**
 * Que pasa en la caja si se aprueba, en palabras del dueno. Un dia ARQUEADO manda sobre el tipo: la caja (Desktop 1251,
 * MotivoDiaArqueado) no cambia nada de un dia ya cuadrado. Medido por Q #20601 y A #20667: las 16 pendientes de EC caen en
 * dias arqueados.
 */
export function queHaceLaCaja(tipo, diaArqueado = null) {
  if (diaArqueado === true) {
    return 'Ese día ya tiene arqueo: la caja NO lo aplica. Queda aprobada pero sin aplicar, hasta que se quite el arqueo de ese día en la caja.';
  }
  return TIPOS_QUE_LA_CAJA_APLICA.includes(String(tipo || ''))
    ? 'La caja lo aplica sola la próxima vez que sincronice.'
    : 'La caja lo marca aprobado al sincronizar y quien lo pidió hace el cambio en la caja (tiene 24 horas).';
}

/**
 * El cuerpo del veredicto. Se arma AQUI y no en la vista: un solo sitio conoce
 * el contrato. Claves en camelCase, porque ASP.NET liga sin distinguir
 * mayusculas pero NO quita guiones bajos (business_id no liga con BusinessId,
 * que es [Required]).
 */
export function cuerpoDelVeredicto({ businessId, veredicto, aprobadorNombre, aprobadorUuid, nota }) {
  if (veredicto !== 'aprobada' && veredicto !== 'rechazada') {
    throw new Error('veredicto tiene que ser aprobada o rechazada, llego: ' + veredicto);
  }
  if (!String(businessId || '').startsWith('biz_')) throw new Error('businessId tiene que empezar por biz_');
  if (!String(aprobadorNombre || '').trim()) throw new Error('aprobadorNombre es obligatorio');
  const n = String(nota || '').trim();
  return {
    businessId: String(businessId),
    veredicto,
    aprobadorNombre: String(aprobadorNombre).trim(),
    aprobadorUuid: aprobadorUuid ? String(aprobadorUuid) : null,
    nota: n ? n : null
  };
}

/**
 * Quien decide, desde el JWT del panel (sin verificar firma: el API lo
 * re-valida). Si el token no trae nombre ni correo, se dice que viene del panel
 * y no se inventa una persona.
 */
export function quienDecide(payload) {
  const p = payload || {};
  const nombre = p.name || p.email || p.preferred_username || '';
  return {
    nombre: String(nombre || 'Propietario desde el panel'),
    uuid: p.sub || p.uid || p.user_id || null
  };
}

/** Lo que pasa despues de pulsar, segun el CrudResult de ApiBase. */
export function leerRespuestaDelVeredicto(res, veredicto, tipo, diaArqueado = null) {
  if (res && res.ok) {
    return {
      estado: 'decidida',
      mensaje: veredicto === 'aprobada'
        ? 'Aprobada. ' + queHaceLaCaja(tipo, diaArqueado)
        : 'Rechazada. No se aplica nada en la caja.'
    };
  }
  const code = res ? res.code : null;
  if (code === 409) return { estado: 'ya-decidida', mensaje: 'Esta solicitud ya tenía una decisión. Recarga para ver cuál.' };
  if (code === 403) return { estado: 'sin-acceso', mensaje: 'Tu usuario no tiene acceso a este negocio: la decisión NO se guardó.' };
  if (code === 404) return { estado: 'no-esta', mensaje: 'Esta solicitud ya no está en el espejo del negocio. Recarga la bandeja.' };
  if (code === 400) return { estado: 'invalida', mensaje: 'El servidor no aceptó la decisión: ' + ((res && res.error) || 'datos incompletos') };
  if (code === 'NETWORK' || code === 'TIMEOUT') return { estado: 'sin-red', mensaje: 'No hubo conexión. La decisión NO se guardó; inténtalo otra vez.' };
  return { estado: 'error', mensaje: 'No se pudo guardar la decisión ahora. No quedó aplicada; inténtalo otra vez.' };
}
